import { MenuOption, Separator } from '@infotech-soe/erp-ui-kit';
import { TDatabaseId } from '@infotech-soe/metaschema';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CategoryTreeType } from '../../../services/category/view-model/CategoryViewModel';
import { CommandListEntryType } from '../../../services/commands/CommandViewModel';
import { ConfirmationMessageType } from '../../../services/commands/UserInteractor';
import { CategoryTreeNode } from '../../../store/slices/dm-trees/CategoryTreeNode';
import {
  NullableStateCell,
  useNullableState,
} from '../../hooks/useNullableState';

import { CatalogsTreeConnectorProps } from './CatalogsTreeContainer';
import { HierarchyTreeConnectorProps } from './HierarchyTreeContainer';
import { TPrivsTreeConnectorProps } from './PrivsTreeContainer';

export const SEPARATOR: Separator = {
  type: CommandListEntryType.SEPARATOR,
};

export const enum TreeCommandName {
  REFRESH = 'refresh',
  HIERARCHY = 'hierarchy',
  CLEAR = 'clear',
  TOGGLE = 'toggle',
  CREATE = 'create',
  DELETE = 'delete',
  RENAME = 'rename',
  CUT = 'cut',
  PASTE = 'paste',
  GRANT_ALL_CATALOG_PRIVS = 'grantAllCatalogPrivs',
  GRANT_ALL_CATEGORY_PRIVS = 'grantAllCategoryPrivs',
  REVOKE_ALL_CATEGORY_PRIVS = 'revokeAllCategoryPrivs',
}

export const PrivsTreeType = 'privs';
export type PrivsTreeType = 'privs';

type TreeType =
  CategoryTreeType.CATALOGS | CategoryTreeType.HIERARCHY | PrivsTreeType;

interface PrivsTreeProps extends Pick<
  TPrivsTreeConnectorProps,
  | 'onGrantAllCatalogPrivs'
  | 'onGrantAllCategoryPrivs'
  | 'onCheckToggle'
  | 'pane'
> {
  isChecked?: boolean;
}

type CatalogsProps = Pick<
  CatalogsTreeConnectorProps,
  | 'tree'
  | 'onCheckToggle'
  | 'onClearChecked'
  | 'onSetHierarchy'
  | 'onShowConfirmation'
>;

type HierarchyProps = Pick<
  HierarchyTreeConnectorProps,
  'tree' | 'onCheckToggle' | 'onClearChecked' | 'onSetHierarchy'
>;

type BaseMenuProps<T extends TreeType> = {
  treeType: T;
  activeNode?: CategoryTreeNode;
  hideCommands?: boolean;
  onRefresh: (nodeId?: TDatabaseId) => void;
};

type Props =
  | (CatalogsProps & BaseMenuProps<CategoryTreeType.CATALOGS>)
  | (HierarchyProps & BaseMenuProps<CategoryTreeType.HIERARCHY>)
  | (PrivsTreeProps & BaseMenuProps<PrivsTreeType>);

export type TreeCommand = {
  type: CommandListEntryType.COMMAND;
  handler: () => void;
} & Exclude<MenuOption<string>, Separator>;

type Return = {
  commands: Record<string, TreeCommand> | null;
  clipboard: NullableStateCell<TDatabaseId>;
};

export const useTreeCommands = (props: Props): Return => {
  const { activeNode, hideCommands, treeType } = props;
  const { formatMessage } = useIntl();
  const clipboard = useNullableState<TDatabaseId>();

  const getLabel = useCallback(
    (name: TreeCommandName, defaultMessage: string): string =>
      formatMessage({
        id: `core.category.tree.catalogs.${name}`,
        defaultMessage,
      }),
    [formatMessage]
  );

  const commands = useMemo<Record<string, TreeCommand> | null>(() => {
    if (hideCommands) return null;

    const res: Partial<Record<TreeCommandName, TreeCommand>> = {};

    if (
      treeType === CategoryTreeType.CATALOGS ||
      treeType === CategoryTreeType.HIERARCHY
    ) {
      res[TreeCommandName.REFRESH] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.REFRESH,
        hotKey: 'F5',
        icon: 'refresh',
        label: getLabel(TreeCommandName.REFRESH, 'Refresh'),
        handler: () => props.onRefresh(activeNode?.id),
      };
      res[TreeCommandName.HIERARCHY] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.HIERARCHY,
        hotKey: 'Ctrl+Space',
        icon: 'list',
        label: getLabel(TreeCommandName.HIERARCHY, 'Hierarchy'),
        handler: () => props.onSetHierarchy(activeNode?.id),
      };
      res[TreeCommandName.CLEAR] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.CLEAR,
        disabled:
          !props.tree.checkedNodes.size && props.tree.hierarchyRootId === null,
        hotKey: 'Ctrl+Delete',
        icon: 'brush',
        label: getLabel(TreeCommandName.CLEAR, 'Clear selection'),
        handler: props.onClearChecked,
      };
      res[TreeCommandName.TOGGLE] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.TOGGLE,
        hotKey: 'Shift+Space',
        icon: 'tree-checked',
        label: getLabel(TreeCommandName.TOGGLE, 'Toggle entire branch'),
        handler: () => props.onCheckToggle(undefined, true),
      };
    }

    if (treeType === CategoryTreeType.CATALOGS) {
      res[TreeCommandName.CREATE] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.CREATE,
        hotKey: 'Insert',
        icon: 'create',
        label: getLabel(TreeCommandName.CREATE, 'New catalog'),
        handler: () =>
          props.onShowConfirmation(ConfirmationMessageType.CREATE_CATALOG),
      };
      res[TreeCommandName.DELETE] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.DELETE,
        disabled: activeNode?.root,
        hotKey: 'Delete',
        icon: 'delete',
        label: getLabel(TreeCommandName.DELETE, 'Delete catalog'),
        handler: () =>
          props.onShowConfirmation(ConfirmationMessageType.DELETE_CATALOG),
      };
      res[TreeCommandName.RENAME] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.RENAME,
        hotKey: 'F2',
        icon: 'edit',
        label: getLabel(TreeCommandName.RENAME, 'Rename catalog'),
        handler: () =>
          props.onShowConfirmation(ConfirmationMessageType.RENAME_CATALOG),
      };
      res[TreeCommandName.CUT] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.CUT,
        disabled: activeNode?.root,
        icon: 'cut',
        label: getLabel(TreeCommandName.CUT, 'Cut'),
        handler: () => {
          if (activeNode) clipboard.setValue(activeNode.id);
        },
      };
      res[TreeCommandName.PASTE] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.PASTE,
        disabled:
          clipboard.isEmpty() ||
          activeNode?.parentId === clipboard.forceValue() ||
          activeNode?.nodePath.has(clipboard.forceValue()),
        icon: 'paste',
        label: getLabel(TreeCommandName.PASTE, 'Paste'),
        handler: () => {
          if (activeNode) {
            props.onShowConfirmation(
              ConfirmationMessageType.MOVE_CATALOG,
              clipboard.forceValueAndClear(),
              activeNode.id
            );
          }
        },
      };
    }

    if (treeType === PrivsTreeType) {
      res[TreeCommandName.REFRESH] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.REFRESH,
        hotKey: 'F5',
        icon: 'refresh',
        label: getLabel(TreeCommandName.REFRESH, 'Refresh'),
        handler: () => props.onRefresh(activeNode?.id),
      };
      if (props.pane === 'catalog')
        res[TreeCommandName.GRANT_ALL_CATALOG_PRIVS] = {
          type: CommandListEntryType.COMMAND,
          value: TreeCommandName.GRANT_ALL_CATALOG_PRIVS,
          disabled: !props.isChecked,
          label: getLabel(
            TreeCommandName.GRANT_ALL_CATALOG_PRIVS,
            'Grant all privs on catalog'
          ),
          icon: 'tree-checked',
          handler: () => {
            if (activeNode) props.onGrantAllCatalogPrivs(activeNode.id);
          },
        };
      res[TreeCommandName.GRANT_ALL_CATEGORY_PRIVS] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.GRANT_ALL_CATEGORY_PRIVS,
        disabled: !props.isChecked,
        label: getLabel(
          TreeCommandName.GRANT_ALL_CATEGORY_PRIVS,
          'Grant all privs on category'
        ),
        icon: 'unlock',
        handler: () => {
          if (activeNode) props.onGrantAllCategoryPrivs(activeNode.id);
        },
      };
      res[TreeCommandName.REVOKE_ALL_CATEGORY_PRIVS] = {
        type: CommandListEntryType.COMMAND,
        value: TreeCommandName.REVOKE_ALL_CATEGORY_PRIVS,
        disabled: !props.isChecked,
        label: getLabel(
          TreeCommandName.REVOKE_ALL_CATEGORY_PRIVS,
          'Revoke all privs on category'
        ),
        icon: 'lock',
        handler: () => {
          if (activeNode) props.onCheckToggle(activeNode.id);
        },
      };
    }
    return res;
  }, [activeNode, clipboard, getLabel, hideCommands, props, treeType]);

  return { commands, clipboard };
};
